import { Input, Subhead } from '@vkontakte/vkui';
import classNames from 'clsx';
import hotkeys from 'hotkeys-js';
import React, { type ReactElement, memo, useCallback, useEffect, useState } from 'react';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { Button } from 'reactApp/ui/Button/Button';
import type { TextInputModalProps } from 'reactApp/ui/TextInputModal/TextInputModal.types';

import styles from './TextInputModal.css';

interface Props extends TextInputModalProps {
    handleAccept(value: string): void;
    handleClose(): void;
    isPhone: boolean;
}

const HOT_KEY_SCOPE_NAME = 'TextInputModal';

export const TextInputModalContent = memo(function TextInputModalContent(props: Props): ReactElement {
    const buttonTheme = props.isPhone ? 'vk' : 'octavius';
    const [value, setValue] = useState('');
    const [error, setError] = useState<null | string>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const validationRes = props.validator?.(e.target.value);

        if (typeof validationRes === 'string') {
            setError(validationRes);
        } else {
            setError(null);
        }

        setValue(e.target.value);
    };

    const handleSubmit = useCallback(() => {
        if (error) {
            return;
        }

        props.handleAccept(value);
    }, [error, props, value]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    useHotKeyScope(HOT_KEY_SCOPE_NAME);
    useEffect(() => {
        hotkeys('enter', HOT_KEY_SCOPE_NAME, handleSubmit);
        return () => {
            hotkeys.unbind('enter', HOT_KEY_SCOPE_NAME, handleSubmit);
        };
    }, [handleSubmit]);

    return (
        <div className={classNames(styles.content, { [styles.content_phone]: props.isPhone })}>
            <div className={styles.title}>{props.title}</div>
            {props.description && <div className={styles.description}>{props.description}</div>}
            <div className={styles.description}>
                <Input
                    type={props.inputType ?? 'text'}
                    placeholder={props.placeholder}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                />
                {error && (
                    <Subhead data-qa-id="text-input-modal-error" className={styles.errorText} weight="3">
                        {error}
                    </Subhead>
                )}
            </div>
            <div className={styles.wrapper_button}>
                <Button
                    disabled={!!error}
                    className={classNames(styles.button)}
                    theme={buttonTheme}
                    primary
                    onClick={handleSubmit}
                    fluid
                    data-name="action"
                >
                    {props.buttonAcceptText}
                </Button>
                <Button className={classNames(styles.button)} theme={buttonTheme} onClick={props.handleClose} fluid data-name="cancel">
                    {props.buttonRejectText}
                </Button>
            </div>
        </div>
    );
});
